import styled from "@emotion/styled";
import { FC } from "react";

import { DotsVert, DropDownFilled, Reply, RosetteDiscountCheckFilled, Star } from "@livechat/design-system-icons";
import { Avatar, DesignToken, Icon, RadiusToken } from "@livechat/design-system-react-components";

import hdAvatar from "assets/img/logo-avatar.png";
import { Icon as HDIcon, IconsEnum } from "components/icon";
import { withVar } from "themes/helpers";

interface IProps {
  name: string;
  zoomed: boolean;
}

export const EmailAnimation: FC<IProps> = ({ name, zoomed }) => {
  const displayedName = name || "Cool Brand";
  return (
    <Wrapper>
      <MailClientContainer className={zoomed ? "zoomed" : ""} data-testid="email-animation">
        <Header>
          <HeaderLeft>
            <Avatar type="image" src={hdAvatar} text={displayedName} size="xxsmall" />
            <AuthorNameWrapper>
              <AuthorName>
                {displayedName}{" "}
                <Icon
                  source={RosetteDiscountCheckFilled}
                  size="xsmall"
                  customColor={withVar(DesignToken.ActionPrimaryDefault)}
                />
              </AuthorName>
              <AuthorToMe>
                to me <Icon source={DropDownFilled} size="xsmall" />
              </AuthorToMe>
            </AuthorNameWrapper>
          </HeaderLeft>
          <HeaderRight>
            <Date>11:29 AM (2 hours ago)</Date>
            <Icon source={Star} size="xsmall" />
            <Icon source={Reply} size="xsmall" />
            <Icon source={DotsVert} size="xsmall" />
          </HeaderRight>
        </Header>
        <Content>
          <HDIcon name={IconsEnum.HelpDeskFullLogo} width="8em" />
          <Message>
            <Headline>Hi, James</Headline>
            <SkeletonWrapper>
              <SkeletonText />
              <SkeletonText />
              <SkeletonText />
              <SkeletonText />
              <SkeletonText width="75%" />
              <SkeletonText width="0" />
              <SkeletonText width="50%" />
            </SkeletonWrapper>
          </Message>
        </Content>
      </MailClientContainer>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  height: 200px;
  font-size: 10px;
  border-radius: ${withVar(RadiusToken[3])};
  background: ${withVar(DesignToken.SurfaceAccentEmphasisLowPositive)};
  pointer-events: none;
  color: ${withVar(DesignToken.ContentBasicPrimary)};
`;

const MailClientContainer = styled.div`
  border-radius: ${withVar(RadiusToken[3])};
  background: ${withVar(DesignToken.SurfaceSecondaryDefault)};
  height: 100%;
  width: 100%;
  position: absolute;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: transform 0.5s ease-in-out;
  transform: scale(1);
  &.zoomed {
    transform: translate(60%, 80%) scale(2);
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Header = styled.div`
  border-radius: ${withVar(RadiusToken[3])} ${withVar(RadiusToken[3])} 0 0;
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  padding: 12px 8px;
  display: flex;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  gap: 1em;
  align-items: center;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: 6px;
  color: ${withVar(DesignToken.ContentBasicSecondary)};
`;

const AuthorNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AuthorName = styled.div`
  display: flex;
  gap: 4px;
  font-weight: 700;
  align-items: center;
  font-size: 1.1em;
`;

const AuthorToMe = styled.div`
  color: ${withVar(DesignToken.ContentBasicSecondary)};
  display: flex;
  align-items: center;
`;

const Date = styled.div`
  display: flex;
  align-items: center;
`;

const Content = styled.div`
  padding: 2em 1em;
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
`;

const Message = styled.div`
  width: 66%;
  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 1em;
  height: 100%;
  min-height: 100%;
`;

const Headline = styled.p`
  font-weight: 700;
  font-size: 1.1em;
`;

const SkeletonText = styled.div<{ width?: string }>`
  width: ${
    // FIXME: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
    (props: any) => props.width || "90%"
  };
  height: 4px;
  border-radius: 4px;
  background: ${withVar(DesignToken.SurfaceTertiaryDefault)};
`;

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
